import { createClient } from '@moonpay/platform-sdk-web';
/** Credentials stay in memory. Dispose on logout, account or environment changes. */
export function createMoonPayWebProvider(options) {
    let client = createClient(options);
    const frames = new Set();
    function current() {
        if (client === null)
            throw new Error('onramp_session_disposed');
        return client;
    }
    async function track(container, setup) {
        current();
        const owned = container.ownerDocument.createElement('div');
        owned.style.width = '100%';
        owned.style.height = '100%';
        container.append(owned);
        let handle = null;
        let disposed = false;
        const frame = {
            dispose() {
                if (disposed)
                    return;
                disposed = true;
                owned.remove();
                frames.delete(frame);
                handle?.dispose();
            },
        };
        // Auth setup may resolve only after OTP; own its DOM before awaiting the handle.
        frames.add(frame);
        try {
            const result = await setup(owned, () => !disposed);
            if (result.ok) {
                if (disposed)
                    result.value.dispose();
                else
                    handle = result.value;
            }
            else
                frame.dispose();
            current();
            return result.ok ? { ok: true, value: { ...result.value, dispose: frame.dispose } } : result;
        }
        catch (error) {
            frame.dispose();
            throw error;
        }
    }
    async function guard(pending) {
        const result = await pending;
        current();
        return result;
    }
    function events(callback, isActive) {
        return (event) => {
            if (client !== null && isActive())
                callback?.(event);
        };
    }
    return {
        getConnection: (...args) => guard(current().getConnection(...args)),
        connect: (...args) => track(args[0].container, (container, isActive) => current().connect({ ...args[0], container, onEvent: events(args[0].onEvent, isActive) })),
        setupAuth: (...args) => track(args[0].container, (container, isActive) => current().setupAuth({ ...args[0], container, onEvent: events(args[0].onEvent, isActive) })),
        getPaymentMethods: (...args) => guard(current().getPaymentMethods(...args)),
        getAssets: (...args) => guard(current().getAssets(...args)),
        getQuote: (...args) => guard(current().getQuote(...args)),
        getCustomer: (...args) => guard(current().getCustomer(...args)),
        submitCustomerKyc: (...args) => guard(current().submitCustomerKyc(...args)),
        getCredentials: (...args) => current().getCredentials(...args),
        setupApplePay: (...args) => track(args[0].container, (container, isActive) => current().setupApplePay({
            ...args[0],
            container,
            onEvent: events(args[0].onEvent, isActive),
        })),
        setupGooglePay: (...args) => track(args[0].container, (container, isActive) => current().setupGooglePay({
            ...args[0],
            container,
            onEvent: events(args[0].onEvent, isActive),
        })),
        setupWidget: (...args) => track(args[0].container, (container, isActive) => current().setupWidget({
            ...args[0],
            container,
            onEvent: events(args[0].onEvent, isActive),
        })),
        setupChallenge: (...args) => track(args[0].container, (container, isActive) => current().setupChallenge({
            ...args[0],
            container,
            onEvent: events(args[0].onEvent, isActive),
        })),
        setupAddCard: (...args) => track(args[0].container, (container, isActive) => current().setupAddCard({
            ...args[0],
            container,
            onEvent: events(args[0].onEvent, isActive),
        })),
        setupBuy: (...args) => track(args[0].container, (container, isActive) => current().setupBuy({ ...args[0], container, onEvent: events(args[0].onEvent, isActive) })),
        deletePaymentMethod: (...args) => guard(current().deletePaymentMethod(...args)),
        resetConnection: (...args) => guard(current().resetConnection(...args)),
        dispose: () => {
            client = null;
            for (const frame of frames)
                frame.dispose();
            frames.clear();
        },
    };
}
export { webFundingMethods } from './discovery/web-device.js';
